/**
 * Estado del widget con Svelte 5 Runes.
 * Una instancia por <selfie-studio> montado (soporta varios widgets por página).
 */
import { fetchInit } from './api';
import { preloadModel, removeBackground, type ModelProgress } from './segmenter';
import type { InitPayload, SelfieTransform, WidgetTemplate } from './types';

export type Step = 'boot' | 'gallery' | 'camera' | 'processing' | 'editor' | 'error';

const INITIAL_TRANSFORM: SelfieTransform = { x: 0, y: 0, scale: 1, rotation: 0 };

export class WidgetState {
	step = $state<Step>('boot');
	error = $state('');

	data = $state<InitPayload | null>(null);
	initReady = $state(false);
	modelReady = $state(false);
	modelProgress = $state(0);

	selectedTemplate = $state<WidgetTemplate | null>(null);
	selfieCanvas = $state<HTMLCanvasElement | null>(null);
	transform = $state<SelfieTransform>({ ...INITIAL_TRANSFORM });

	/** Listo para entrar a la galería: branding cargado y modelo descargado. */
	ready = $derived(this.initReady && this.modelReady);

	constructor(
		private apiUrl: string,
		private clientToken: string,
		private campaignToken: string
	) {}

	/**
	 * Boot del widget: el fetch de /init y la descarga del modelo de
	 * segmentación corren EN PARALELO mientras se muestra el loader.
	 */
	async boot() {
		this.step = 'boot';
		this.error = '';

		const initTask = fetchInit(this.apiUrl, this.clientToken, this.campaignToken)
			.then((payload) => {
				this.data = payload;
				this.initReady = true;
			});

		const modelTask = preloadModel((progress: ModelProgress) => {
			if (progress.status === 'progress' && typeof progress.progress === 'number') {
				this.modelProgress = Math.round(progress.progress);
			}
		}).then(() => {
			this.modelReady = true;
			this.modelProgress = 100;
		});

		try {
			await Promise.all([initTask, modelTask]);
			this.step = 'gallery';
		} catch (err: any) {
			this.error = err?.message || 'No se pudo inicializar el widget.';
			this.step = 'error';
		}
	}

	selectTemplate(template: WidgetTemplate) {
		this.selectedTemplate = template;
		this.step = 'camera';
	}

	/** Recibe la captura de cámara y la procesa localmente (sin servidor). */
	async processSelfie(imageDataUrl: string) {
		this.step = 'processing';
		try {
			this.selfieCanvas = await removeBackground(imageDataUrl);
			this.transform = { ...INITIAL_TRANSFORM };
			this.step = 'editor';
		} catch (err: any) {
			this.error = err?.message || 'No se pudo procesar la imagen.';
			this.step = 'error';
		}
	}

	// ── Controles del workspace (capa 2) ────────────────────────────────────
	move(dx: number, dy: number) {
		this.transform = { ...this.transform, x: this.transform.x + dx, y: this.transform.y + dy };
	}

	zoom(factor: number) {
		const scale = Math.min(4, Math.max(0.2, this.transform.scale * factor));
		this.transform = { ...this.transform, scale };
	}

	rotate(degrees: number) {
		this.transform = { ...this.transform, rotation: degrees };
	}

	resetTransform() {
		this.transform = { ...INITIAL_TRANSFORM };
	}

	retake() {
		this.selfieCanvas = null;
		this.step = 'camera';
	}

	backToGallery() {
		this.selfieCanvas = null;
		this.selectedTemplate = null;
		this.step = 'gallery';
	}
}
